/**
 * Pure helpers for the F1.6.0 contract review screens. CR-2026-CONTRACT-REVIEW (2026-10-15). Exported for tests.
 * The server decides everything; these only shape what it returned and mirror its checks for a faster answer.
 */

export const STATUS_LABEL = {
  DRAFT: "Draft", SUBMITTED: "Submitted for sign-off", ACCEPTED: "Accepted", HELD: "On hold", REJECTED: "Rejected", SUPERSEDED: "Superseded",
};
export const STATUS_COLOR = { DRAFT: "default", SUBMITTED: "info", ACCEPTED: "success", HELD: "warning", REJECTED: "error", SUPERSEDED: "default" };

export const STATE_LABEL = {
  PROPOSED: "Proposed", CONFLICT: "Conflict", NOT_STATED: "Not stated", MANUAL: "To answer", CONFIRMED: "Confirmed", NA: "N/A", DECISION: "For signatory",
};
export const STATE_COLOR = { PROPOSED: "info", CONFLICT: "error", NOT_STATED: "warning", MANUAL: "default", CONFIRMED: "success", NA: "default", DECISION: "secondary" };

export const REVIEW_STATUS_LABEL = {
  STATED: "Stated", NA: "N/A", DECISION_REQUIRED: "Decision required", NOT_STATED_RFI: "Not stated (RFI)", OPEN: "Open",
};
export const SECTION_STATUS = {
  STATED: { label: "Stated", color: "success" },
  PARTLY_STATED: { label: "Partly stated", color: "warning" },
  NOT_STATED: { label: "Not stated", color: "error" },
  DECISION_REQUIRED: { label: "Decision required", color: "secondary" },
};
export const SOURCE_ROLE_LABEL = {
  CONTRACT: "Contract / PO", SPEC: "Specification", DRAWINGS: "Drawings", PROPOSAL: "Proposal", ESTIMATE: "Estimate recap", ADDENDUM: "Addendum", OTHER: "Other",
};

/** "HEAT_TO_PIECE,SEATED" -> "Heat to piece, Seated" */
export function optLabel(v) {
  if (v === null || v === undefined || v === "") return "";
  return String(v).split(",").map((x) => x.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase())).join(", ");
}

/** The answer as a person reads it. */
export function answerText(a) {
  if (!a) return "";
  if (a.state === "NA") return `N/A: ${a.naReason || ""}`;
  if (a.state === "DECISION") {
    const d = a.decisionOutcome;
    return `For signatory decision: ${a.decisionNote || ""}${d ? `. Decided by ${d.byName}: ${d.outcome}` : ""}`;
  }
  if (a.state === "NOT_STATED") return a.rfi ? `Not stated (${a.rfi.number}, ${String(a.rfi.status).toLowerCase()})` : "Not stated in the documents";
  if (a.state === "CONFLICT") return `Conflict: ${(a.alternatives || []).map((x) => (a.type === "text" ? x : optLabel(x))).join(" / ")}`;
  if (a.state === "MANUAL") return "";
  if (a.value === null || a.value === undefined) return "";
  if (a.type === "yn") return a.value === "Y" ? "Yes" : a.value === "N" ? "No" : a.value;
  if (a.type === "choice") return optLabel(a.value);
  return a.value;
}

/** First citation as "contract.pdf p.2" (or "SFM review sources"). */
export function citationText(c) {
  if (!c) return "";
  if (c.sfm) return `SFM ${c.sfm}`;
  return `${c.name || "document"} p.${c.page}${c.negated ? " (negated wording)" : ""}`;
}

/** What a reviewer may do with an answer in a DRAFT revision. */
export function answerActions(a, canEdit, status) {
  if (!canEdit || status !== "DRAFT" || !a) return [];
  const out = [];
  if (a.state === "PROPOSED") out.push("confirm");
  if (a.state === "CONFLICT" && !a.rfi) out.push("resolve");
  if (!a.rfi || a.state === "NOT_STATED") out.push("answer");
  out.push("na");
  if (!a.rfi && a.state !== "DECISION") out.push("decision");
  if (!a.rfi && ["NOT_STATED", "CONFLICT", "MANUAL", "PROPOSED"].includes(a.state)) out.push("rfi");
  if (!a.rfi && ["CONFIRMED", "NA", "DECISION"].includes(a.state)) out.push("reopen");
  return out;
}

/**
 * Review P2-5: yes/no answers and the traceability level are confirmed one at a time in a dialog that shows every
 * quote; only other cited proposals can be confirmed a section at a time (mirrors the server).
 */
export const confirmNeedsDialog = (a) => !!a && (a.type === "yn" || a.key === "MAT_TRACE");
export const sectionConfirmable = (a) => !!a && a.state === "PROPOSED" && (a.citations || []).length > 0 && !confirmNeedsDialog(a);

/** Mirrors the server's answer checks. Returns an error message or null. */
export function answerProblem(action, a, f) {
  const basis = String(f.basisNote || "").trim();
  if (action === "answer") {
    const v = String(f.value || "").trim();
    if (!v) return "Give the answer.";
    if (a.type === "yn" && !["Y", "N"].includes(v.toUpperCase())) return "Answer Yes or No.";
    if (a.type === "choice") {
      const parts = v.split(",");
      if (!parts.every((p) => (a.opts || []).includes(p))) return "Choose from the list.";
      if (!a.multi && parts.length > 1) return "Choose one.";
    }
    if (v.length > 300) return "300 characters at most.";
    if (basis.length < 4) return "Give the basis: where it is stated, or who decided it.";
  }
  if (action === "resolve") {
    if (!(a.alternatives || []).map(String).includes(String(f.value))) return "Choose one of the conflicting answers.";
    if (basis.length < 4) return "Say why this answer wins over the other.";
  }
  if (action === "na" && String(f.naReason || "").trim().length < 4) return "Say why the question does not apply.";
  if (action === "decision") {
    if (!f.decisionSlot) return "Choose which signatory decides it.";
    if (String(f.decisionNote || "").trim().length < 10) return "Describe the decision (at least 10 characters).";
  }
  if (action === "rfi") {
    if (!String(f.requestedOf || "").trim()) return "Who is the RFI for?";
    if (String(f.question || "").trim().length < 10) return "Write the question (at least 10 characters).";
  }
  return null;
}

/** PATCH body for an answer action (resolve = confirm with the chosen value). */
export function answerBody(action, a, f) {
  const b = { rowVersion: a.rowVersion };
  if (action === "confirm") return { ...b, action: "confirm" };
  if (action === "resolve") return { ...b, action: "confirm", value: f.value, basisNote: String(f.basisNote).trim() };
  if (action === "answer") return { ...b, action: "answer", value: a.type === "text" ? String(f.value).trim() : String(f.value).trim().toUpperCase(), basisNote: String(f.basisNote).trim() };
  if (action === "na") return { ...b, action: "na", naReason: String(f.naReason).trim() };
  if (action === "decision") return { ...b, action: "decision", decisionSlot: f.decisionSlot, decisionNote: String(f.decisionNote).trim() };
  if (action === "reopen") return { ...b, action: "reopen" };
  return b;
}

/** Lines the signed-in user may sign now. */
export function signableSlots(review, mySlots) {
  if (!review || review.status !== "SUBMITTED") return [];
  return (review.slots || []).filter((s) => !s.unused && !s.signature && (mySlots || []).includes(s.key)).map((s) => s.key);
}

/** Source types that reopen a submitted or accepted review (mirrors the server, review P2-2). */
export const REOPENING_ROLES = ["CONTRACT", "SPEC", "ADDENDUM", "PROPOSAL"];

/** What withdrawing a document does (mirrors the server, 027 P2-6). */
export function withdrawEffect(role, status) {
  if (!status) return "The document stays on record and is marked withdrawn; capture will not read it.";
  if (status === "DRAFT") return "The document stays on record (file and fingerprint) and capture runs again without it. Answers it supported come back for review.";
  if (REOPENING_ROLES.includes(role)) return "This changes the contract basis: a new revision is started. New release, package activation and material issue wait until it is accepted again; stage and station work on pieces already released continue.";
  return "The document stays on record and is marked withdrawn. The current revision is not reopened; capture stops reading it from the next revision.";
}

/**
 * Mirrors the sign checks (review P2-3): a decision is chosen (nothing is pre-selected), a reason for Hold or Reject,
 * and, to accept, an outcome for every decision sent to this line.
 */
export function signProblem(decision, comment, items, outcomes) {
  if (!["ACCEPT", "HOLD", "REJECT"].includes(decision)) return "Choose Accept, Hold or Reject.";
  for (const it of items || []) {
    const o = String((outcomes || {})[it.key] || "").trim();
    if (decision !== "ACCEPT" && !o) continue;
    if (o.length < 10) return `Record your decision on "${it.question}" (at least 10 characters).`;
  }
  if (decision !== "ACCEPT" && String(comment || "").trim().length < 10) return "Say why (at least 10 characters). It prints on the F1.6.0 record.";
  return null;
}

/** Mirrors CR-SOD-01: the person who signed one of PM / QC Manager cannot sign the other. */
export function segregationProblem(review, slot, userId) {
  const other = slot === "QC_MGR" ? "PM" : slot === "PM" ? "QC_MGR" : null;
  if (!other) return null;
  const s = (review.slots || []).find((x) => x.key === other);
  if (s && s.signature && Number(s.signature.userId) === Number(userId)) {
    return "The QC Manager and Project Manager lines must be signed by different people.";
  }
  return null;
}

/**
 * The management verdict: READY only when the revision is ACCEPTED. Otherwise the reasons, in the order a manager acts on them.
 */
export function verdict(data) {
  const r = data && data.review;
  if (!r) return { ready: false, label: "NOT READY", reasons: ["No F1.6.0 contract review has been started. Add the contract and specifications."] };
  if (r.status === "ACCEPTED") return { ready: true, label: "ACCEPTED", reasons: [] };
  const reasons = [];
  const t = r.totals || {};
  if (r.status === "DRAFT") reasons.push("The review is still in draft.");
  if (r.status === "SUBMITTED") {
    const left = (r.slots || []).filter((s) => !s.unused && !s.signature).map((s) => s.label);
    if (left.length) reasons.push(`Waiting for ${left.length} signature${left.length === 1 ? "" : "s"}: ${left.join(", ")}.`);
  }
  if (r.status === "HELD") reasons.push("A signatory put the review on hold. The PM revises it.");
  if (r.status === "REJECTED") reasons.push("A signatory rejected the review. The PM revises it.");
  if (t.OPEN) reasons.push(`${t.OPEN} question${t.OPEN === 1 ? "" : "s"} still open.`);
  if (t.DECISION_REQUIRED) reasons.push(`${t.DECISION_REQUIRED} decision${t.DECISION_REQUIRED === 1 ? "" : "s"} for the signatories.`);
  if (r.openRfis) reasons.push(`${r.openRfis} RFI${r.openRfis === 1 ? "" : "s"} not closed.`);
  return { ready: false, label: "NOT READY", reasons };
}

/** Questions that are not stated, for the management gap list. */
export function gaps(review) {
  return ((review && review.answers) || []).filter((a) => ["NOT_STATED_RFI", "OPEN", "DECISION_REQUIRED"].includes(a.reviewStatus));
}

/** Mirrors the upload checks. */
export function sourceProblem(file, role, roles) {
  if (!file) return "Choose a PDF.";
  if (!/\.pdf$/i.test(file.name || "")) return "Only PDF files can be added.";
  if (file.size > 50 * 1024 * 1024) return "The PDF is larger than 50 MB.";
  if (!(roles || []).includes(role)) return "Say what the document is.";
  return null;
}

export const fmtLbs = (n) => (n === null || n === undefined ? "not recorded" : `${Math.round(n).toLocaleString("en-US")} lb`);
